import { useState, useContext, useEffect } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import { Backdrop, Box, CircularProgress, Link, Stack } from "@mui/material";

import { LoginHeader as NewLoginHeader } from "./v2/login/LoginHeader";
import { CourseStructureContext } from "../context/CourseStructureContext";
import { UserContext } from "../context/UserContext";
import { CourseStructureCtx, UserCtx } from "../interfaces";
import { LoginPage as NewLoginPage } from "../pages/v2/LoginPage";
import { SelfRegistration } from "./v2/self-registration/SelfRegistration";
import { TermsAndConditionsText } from "./TermsAndConditionsText";
import { ErrorPage } from "../pages/ErrorPage";
import { ScheduledMaintenance } from "../pages/ScheduledMaintenancePage";
import { FAQPage } from "../pages/v3/FAQPage";
import { getSupportLink } from "../Helpers";
import { formatDateMst, formatTimeMst } from "./utils/MSTDateTimeFormatter";

export const MacsRouterUnauth = () => {
  const [showProgress, setShowProgress] = useState(false);
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const userCtx = useContext(UserContext) as UserCtx;
  useEffect(() => {
    if (
      userCtx.currentUserEndpointIsLoading === true ||
      courseStructureCtx?.modulesEndpointIsLoading === true
    ) {
      setShowProgress(true);
      return;
    } else {
      setShowProgress(false);
    }
  }, [
    userCtx?.currentUserEndpointIsLoading,
    courseStructureCtx?.modulesEndpointIsLoading,
  ]);

  if (showProgress) {
    return (
      <Backdrop open>
        <CircularProgress />
      </Backdrop>
    );
  }

  const maintenanceWindow = userCtx.maintenanceWindow;
  const now = new Date();

  const underScheduledMaintenance = !!(
    maintenanceWindow &&
    new Date(maintenanceWindow.maintenance_from) <= now &&
    now <= new Date(maintenanceWindow.maintenance_to)
  );

  const toTime = maintenanceWindow
    ? formatTimeMst(maintenanceWindow.maintenance_to)
    : undefined;

  const toDate = maintenanceWindow
    ? formatDateMst(maintenanceWindow.maintenance_to)
    : undefined;

  const maintenanceMessage =
    maintenanceWindow?.custom_maintenance_message != null
      ? maintenanceWindow?.custom_maintenance_message
      : `This is scheduled maintenance to improve your experience. We expect to be back by ${toTime} MST on ${toDate}. Thanks for your patience while we work!`;

  return (
    <>
      <Switch>
        <Route
          exact
          path="/scheduled-maintenance"
          render={() =>
            underScheduledMaintenance ? (
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  height: "100vh",
                }}>
                <NewLoginHeader showLogin={false} />
                <Box sx={{ position: "relative", flexGrow: 1 }}>
                  <ScheduledMaintenance
                    title="We’re making some tune-ups"
                    description={maintenanceMessage}
                  />
                </Box>
              </Box>
            ) : (
              <Redirect to="/" />
            )
          }
        />

        {underScheduledMaintenance && <Redirect to="/scheduled-maintenance" />}

        {/* ================= NORMAL APP ROUTES ================= */}

        <Route exact path="/error">
          <div>
            Some Error Occurred. redirected from server this route will be used when LTI
            token has expired from server side
          </div>
        </Route>
        <Route exact path="/account-disabled">
          <>
            <Box
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                height: "100vh",
              }}>
              <NewLoginHeader showLogin={false} />
              <Box sx={{ position: "relative", flexGrow: 1 }}>
                <ErrorPage
                  title="Your account is disabled"
                  description={
                    <>
                      Please contact{" "}
                      <Link
                        href={getSupportLink(true)}
                        target="_blank"
                        color="#FFF"
                        sx={{
                          textDecorationColor: "#FFF",
                          "&:hover": {
                            color: "#007CBF",
                            textDecorationColor: "#007CBF",
                          },
                        }}>
                        Digit Support
                      </Link>{" "}
                      for more information.
                    </>
                  }
                />
              </Box>
            </Box>
          </>
        </Route>
        <Route exact path="/session-ended">
          <>
            <Box
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                height: "100vh",
              }}>
              <NewLoginHeader showLogin={false} />
              <Box sx={{ position: "relative", flexGrow: 1 }}>
                <ErrorPage
                  title="Your session has ended"
                  description="Don’t worry, your progress was saved. Re-launch the assignment from your class portal (i.e. Canvas, Studyforge) to log back in."
                />
              </Box>
            </Box>
          </>
        </Route>
        <Route exact path="/self-registration">
          <Stack direction="column" height="100vh">
            <NewLoginHeader showLogin={true} />
            <SelfRegistration screen="coursecode" />
          </Stack>
        </Route>
        <Route exact path="/self-registration/registration-form">
          <Stack direction="column" height="100vh">
            <NewLoginHeader showLogin={true} />
            <SelfRegistration screen="registrationform" />
          </Stack>
        </Route>
        <Route exact path="/self-registration/verify-email">
          <Stack direction="column" height="100vh">
            <NewLoginHeader showLogin={true} />
            <SelfRegistration screen="verifyemail" />
          </Stack>
        </Route>
        <Route exact path="/self-registration/account-exists">
          <Stack direction="column" height="100vh">
            <NewLoginHeader showLogin={true} />
            <SelfRegistration screen="accountexists" />
          </Stack>
        </Route>

        <Route exact path="/demo-registration">
          <Stack direction="column" height="100vh">
            <NewLoginHeader showLogin={true} />
            <SelfRegistration screen="coursecode" isDemoMode={true} />
          </Stack>
        </Route>
        <Route exact path="/demo-registration/registration-form">
          <Stack direction="column" height="100vh">
            <NewLoginHeader showLogin={true} />
            <SelfRegistration screen="registrationform" isDemoMode={true} />
          </Stack>
        </Route>
        <Route exact path="/demo-registration/verify-email">
          <Stack direction="column" height="100vh">
            <NewLoginHeader showLogin={true} />
            <SelfRegistration screen="verifyemail" isDemoMode={true} />
          </Stack>
        </Route>
        <Route exact path="/demo-registration/account-exists">
          <Stack direction="column" height="100vh">
            <NewLoginHeader showLogin={true} />
            <SelfRegistration screen="accountexists" isDemoMode={true} />
          </Stack>
        </Route>
        <Route exact path="/terms-of-service">
          <TermsAndConditionsText />
        </Route>
        <Route exact path="/faq">
          <Stack direction="column" height="100vh">
            <NewLoginHeader showLogin={false} showDemo={false} />
            <FAQPage key="faq-page" />
          </Stack>
        </Route>
        <Route exact path="/faq/search">
          <Stack direction="column" height="100vh">
            <NewLoginHeader showLogin={false} showDemo={false} />
            <FAQPage key="faq-page" search />
          </Stack>
        </Route>
        <Route exact path="/faq/:courseTag">
          <Stack direction="column" height="100vh">
            <NewLoginHeader showLogin={false} showDemo={false} />
            <FAQPage key="faq-page" singleCourseTag />
          </Stack>
        </Route>
        <Route path="/">
          <>
            <NewLoginHeader showLogin={false} showDemo={true} />
            <NewLoginPage />
          </>
        </Route>
      </Switch>
    </>
  );
};
